import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next';
import { Badge, ButtonGroup, Button, Card, CardBody, CardTitle } from "reactstrap";
import { Link } from 'react-router-dom';
import CopyButton from '../CopyButton/CopyButton';
import { Log, Ticket, Tickettype } from '../../models';
import { Utils, Screenings } from '../../services';
import { countryIsoFromValue, countryDisplayName } from '../../services/countryIso';
import ImportInvalidMessage from '../ImportInvalidMessage/ImportInvalidMessage';
import SyncFileImportSection from '../SynchronisationPanel/SyncFileImportSection/SyncFileImportSection';
import * as FileSaver from 'file-saver';
import * as XLSX from '@e965/xlsx';
import {
    loadTickettypes,
    loadEmailLayouts,
    importTickets,
    clearLastTicketImportLogs,
} from '../../redux';
import moment from 'moment';
import { fitWorksheetColumns } from '../../services/excelExport';
import {
    entity_matches_id_or_ref,
    find_pricing_key,
    preferred_public_id,
} from '../../services/entityPublicId';
import {
    IMPORT_HEADER_LANGS,
    annotateHeaderComments,
    normalizeHeaderLabel,
} from '../../services/importSpreadsheetHeaders';

import './TicketsRawImporter.css';

const LEGACY_CELLS = [
    'nb', 'type', 'pricing', 'title', 'firstname', 'lastname', 'email', 'street', 'zip', 'city',
    'country', 'image_url', 'cellphone', 'sex', 'age', 'language', 'company', 'screening', 'row',
    'seat', 'prefix',
];

const CELLS = [
    'nb', 'type', 'pricing', 'title', 'firstname', 'lastname', 'email', 'disable_email_notifications',
    'street', 'zip', 'city', 'country', 'image_url', 'cellphone', 'sex', 'age', 'language', 'company',
    'screening', 'row', 'seat', 'prefix', 'ticket_id', 'wallet_amount', 'window_nbookings',
    'window_stop_at',
];

/** English technical headers from older sample files (backward compatible). */
const LEGACY_HEADER_LABELS = {
    nb: 'Nb',
    type: 'Tickettype',
    pricing: 'Pricing',
    title: 'Title',
    firstname: 'Firstname',
    lastname: 'Lastname',
    email: 'Email',
    disable_email_notifications: 'Disable email notifications',
    street: 'Street',
    zip: 'Zip',
    city: 'City',
    country: 'Country',
    image_url: 'Photo',
    cellphone: 'Cellphone',
    sex: 'Sex',
    age: 'Age',
    language: 'Language',
    company: 'Company',
    screening: 'Screening',
    row: 'Row',
    seat: 'Seat',
    prefix: 'Prefix',
    ticket_id: 'TicketID',
    wallet_amount: 'Wallet amount',
    window_nbookings: 'Window nbookings',
    window_stop_at: 'Window stop at',
};

const sanitizeCell = value => {
    if (value === null || typeof value === 'undefined')
       return '';

    return String(value).replace(/[\u00A0\u202F]/g, ' ').trim();
};

const sanitizeIdentifier = value => sanitizeCell(value);

const build_sample_row = (overrides, header_labels) => {
    const row = {};
    CELLS.forEach(key => {
        row[header_labels[key]] = overrides?.[key] ?? '';
    });
    return row;
};

const screeningsCache = {};
const FILE_EXTENSIONS = [ 'csv', 'xls', 'xlsx', 'ods', 'numbers' ];

const TICKET_ID_REGEX = /^(\d{1,6})-([abcdefghijkmnpqrstuvwxyz23456789]{1,6})$/i;

const parseTicketId = value => {
    const normalized = sanitizeCell(value);
    if (!normalized.length)
        return null;

    const match = normalized.match(TICKET_ID_REGEX);
    if (!match)
        return null;

    return {
        vdr_auth_serial: match[1],
        vdr_auth_key: match[2],
    };
};

const mapRowToCells = (row, headerRow, header_aliases) => {
    if (headerRow?.length) {
        const mapped = {};
        headerRow.forEach((header, index) => {
            const key = header_aliases[normalizeHeaderLabel(header)];
            if (key)
                mapped[key] = sanitizeCell(row[index]);
        });
        if (Object.keys(mapped).length)
            return mapped;
    }

    const layout = row.length >= CELLS.length ? CELLS : LEGACY_CELLS;
    const mapped = {};
    layout.forEach((property, propertyIndex) => {
        mapped[property] = sanitizeCell(row[propertyIndex]);
    });
    return mapped;
};

const parseBooleanCell = value => {
    const normalized = sanitizeCell(value).toLowerCase();
    if (!normalized.length)
        return null;
    if (['true', '1', 'yes', 'y'].includes(normalized))
        return true;
    if (['false', '0', 'no', 'n'].includes(normalized))
        return false;
    return null;
};

const getScreeningRemainingCapacity = (screening, tickettypes = []) => {
    const buckets = screening?.buckets || [];
    if (!buckets.length)
        return null;

    const now = Date.now();
    const demandedTickettypes = new Set((tickettypes || []).filter(Boolean));

    let hasCapacityBucket = false;
    let remaining = 0;
    buckets.forEach(bucket => {
        const rules = bucket?.rules || {};
        const notBeforeMs = rules?.not_before ? new Date(rules.not_before).getTime() : null;
        const notAfterMs = rules?.not_after ? new Date(rules.not_after).getTime() : null;

        // Ignore stash-like or out-of-window buckets.
        if (Number.isFinite(notAfterMs) && notAfterMs <= 0)
            return;
        if (Number.isFinite(notBeforeMs) && notBeforeMs > now)
            return;
        if (Number.isFinite(notAfterMs) && notAfterMs < now)
            return;

        // If bucket is restricted to ticket types, keep it only when relevant.
        const onlyForTickettypes = Array.isArray(rules?.only_for_tickettypes) ? rules.only_for_tickettypes : null;
        if (onlyForTickettypes?.length) {
            const hasMatchingTickettype = onlyForTickettypes.some(tickettype => demandedTickettypes.has(tickettype));
            if (!hasMatchingTickettype)
                return;
        }

        const available = Number(bucket.available);
        if (Number.isFinite(available)) {
            hasCapacityBucket = true;
            remaining += Math.max(0, available);
            return;
        }

        const totalCapacity = Number(bucket.total_capacity);
        if (!Number.isFinite(totalCapacity) || totalCapacity <= 0)
            return;

        hasCapacityBucket = true;

        const confirmed = Number(bucket.confirmed);
        const unconfirmed = Number(bucket.unconfirmed);
        const consumed = (Number.isFinite(confirmed) ? confirmed : 0) + (Number.isFinite(unconfirmed) ? unconfirmed : 0);
        remaining += Math.max(0, totalCapacity - consumed);
    });

    if (!hasCapacityBucket)
        return null;

    return remaining;
};

const MAX_EXPECTED_IDS = 12;

const hasInvalidField = (ticket, field) =>
    ticket.invalidProperties?.some(p => p?.field === field);

const listPricingKeysWithZeroPrice = pricings =>
    Object.keys(pricings || {})
        .filter(k => {
            const p = pricings[k]?.price;
            if (!p || typeof p !== 'object')
                return false;
            const keys = Object.keys(p);
            if (!keys.length)
                return false;
            return p[keys[0]] === 0;
        })
        .sort();

const preferredPricingIds = pricings =>
    Object.keys(pricings || {})
        .map(key => preferred_public_id(key, pricings[key]?.refs))
        .sort();

const joinPreferredPricingIds = pricings =>
    preferredPricingIds(pricings).join(', ');

const truncateSortedIds = (ids, formatRest) => {
    const sorted = [...new Set(ids)].filter(Boolean).sort();
    if (!sorted.length)
        return '';
    if (sorted.length <= MAX_EXPECTED_IDS)
        return sorted.join(', ');
    const head = sorted.slice(0, MAX_EXPECTED_IDS).join(', ');
    return formatRest ? `${head}${formatRest(sorted.length - MAX_EXPECTED_IDS)}` : head;
};

const MAX_ID_BADGES_DISPLAY = 24;
const MAX_TYPE_BADGES_DISPLAY = 5;

const SCREENING_TAB_ITEMLIST_KEY = {
    buckets: 'bucket',
    pricings: 'pricing',
    general: 'general'
};

const InvalidKvRow = ({ label, foundRow, children }) => (
    <div className={`TicketsRawImporter-kvRow${foundRow ? ' TicketsRawImporter-kvRow--found' : ''}`}>
        <span className="TicketsRawImporter-kvLabel text-muted small">{label}</span>
        <div className="TicketsRawImporter-kvValue">{children}</div>
    </div>
);

const TicketImportInvalidDetail = ({ entry, label, formatFound, t }) => {
    const hasIdBadges = Array.isArray(entry.idBadgesExpected) && entry.idBadgesExpected.length > 0;
    const screeningLink = entry.screeningLink;
    const tickettypeLink = entry.tickettypeLink;
    const cap = entry.capacityPresentation;

    const screeningTabLabel = screeningLink?.tab &&
        t(`screenings.edit.itemlist.${SCREENING_TAB_ITEMLIST_KEY[screeningLink.tab] || 'general'}`);

    const show_tickettypes_list_link = entry.field === 'type';
    const bottomLinks = (show_tickettypes_list_link || screeningLink?.id || tickettypeLink?.id) ? (
        <div className="d-flex flex-wrap align-items-center mt-2 w-100 TicketsRawImporter-invalidLinks">
            {show_tickettypes_list_link && (
                <Link className="small mr-3" to="/tickettypes">
                    <i className="fa fa-external-link" />&nbsp;
                    {t('tickets.import.link_tickettypes_list')}
                </Link>
            )}
            {screeningLink?.id && (
                <Link
                    className="small mr-3"
                    to={`/screenings/edit/${encodeURIComponent(screeningLink.id)}/${encodeURIComponent(screeningLink.tab)}`}
                >
                    <i className="fa fa-external-link" />&nbsp;
                    {t('tickets.import.link_screening_tab', { tab: screeningTabLabel })}
                </Link>
            )}
            {tickettypeLink?.id && (
                <Link
                    className="small mr-3"
                    to={`/tickettypes/edit/${encodeURIComponent(tickettypeLink.id)}`}
                    title={t('tickets.import.link_tickettype_a11y')}
                >
                    <i className="fa fa-external-link" />&nbsp;
                    {tickettypeLink.label || t('tickets.import.link_tickettype')}
                </Link>
            )}
        </div>
    ) : null;

    const linkInline = (
        <>
            {screeningLink?.id && (
                <Link
                    className="small ml-2"
                    to={`/screenings/edit/${encodeURIComponent(screeningLink.id)}/${encodeURIComponent(screeningLink.tab)}`}
                >
                    <i className="fa fa-external-link" />&nbsp;
                    {t('tickets.import.link_screening_tab', { tab: screeningTabLabel })}
                </Link>
            )}
            {tickettypeLink?.id && (
                <Link
                    className="small ml-2"
                    to={`/tickettypes/edit/${encodeURIComponent(tickettypeLink.id)}`}
                    title={t('tickets.import.link_tickettype_a11y')}
                >
                    <i className="fa fa-external-link" />&nbsp;
                    {tickettypeLink.label || t('tickets.import.link_tickettype')}
                </Link>
            )}
        </>
    );

    if (cap && entry.field === 'screening_capacity') {
        const foundText = cap.multiLine
            ? t('tickets.import.screening_capacity_found_lines', { count: cap.demanded })
            : t('tickets.import.screening_capacity_found_single', { count: cap.demanded });
        const expectedText = t('tickets.import.screening_capacity_expected_short', { count: cap.remaining });
        return (
            <div className="TicketsRawImporter-invalidDetail">
                <p className="small mb-2">
                    {t('tickets.import.screening_capacity_invalid_title', { name: cap.screeningName })}
                </p>
                <InvalidKvRow label={t('tickets.import.invalid_found')} foundRow>
                    <span className="small">{foundText}</span>
                </InvalidKvRow>
                <InvalidKvRow label={t('tickets.import.invalid_expected')}>
                    <span className="small">{expectedText}</span>
                </InvalidKvRow>
                {bottomLinks}
            </div>
        );
    }

    if (!hasIdBadges) {
        const found = formatFound(entry.found);
        const expected = entry.expected?.length
            ? entry.expected
            : t('tickets.import.expected_unknown');
        if (entry.idBadgeFound) {
            return (
                <div className="TicketsRawImporter-invalidDetail TicketsRawImporter-invalidDetail--textBadges">
                    <div className="mb-2">
                        <span className="small">
                            {t('tickets.import.cell_invalid_intro', { field: label })}
                        </span>
                    </div>
                    <InvalidKvRow label={t('tickets.import.invalid_found')} foundRow>
                        <Badge color="danger" className="px-2 py-1 font-weight-bold small">
                            {entry.idBadgeFound}
                        </Badge>
                    </InvalidKvRow>
                    <InvalidKvRow label={t('tickets.import.invalid_expected')}>
                        <span className="small">{expected}</span>
                    </InvalidKvRow>
                    {bottomLinks}
                </div>
            );
        }
        return (
            <div className="TicketsRawImporter-invalidDetail">
                <div className="d-flex flex-wrap align-items-baseline">
                    <span>
                        {t('tickets.import.field_is_invalid_detail', {
                            field: label,
                            found,
                            expected
                        })}
                    </span>
                    {linkInline}
                </div>
            </div>
        );
    }

    const fullExpected = entry.idBadgesExpected;
    const maxExpectedBadges = entry.field === 'type' ? MAX_TYPE_BADGES_DISPLAY : MAX_ID_BADGES_DISPLAY;
    const shown = fullExpected.slice(0, maxExpectedBadges);
    const restCount = Math.max(0, fullExpected.length - maxExpectedBadges);

    return (
        <div className="TicketsRawImporter-invalidDetail">
            <div className="mb-2">
                <span className="small">
                    {t('tickets.import.cell_invalid_intro', { field: label })}
                </span>
            </div>
            <InvalidKvRow label={t('tickets.import.invalid_found')} foundRow>
                {entry.idBadgeFound ? (
                    <Badge color="danger" className="px-2 py-1 font-weight-bold small">
                        {entry.idBadgeFound}
                    </Badge>
                ) : (
                    <span className="small">{formatFound(entry.found)}</span>
                )}
            </InvalidKvRow>
            <InvalidKvRow label={t('tickets.import.invalid_expected')}>
                <div className="TicketsRawImporter-expectedBadges">
                    {shown.map(id => (
                        <span key={id} className="d-inline-flex align-items-center">
                            <CopyButton size="sm" badge color="info" textToCopy={id} title={id} />
                            {entry.field === 'type' && (
                                <Link
                                    className="ml-1 small text-nowrap"
                                    to={`/tickettypes/edit/${encodeURIComponent(id)}`}
                                    title={t('tickets.import.link_tickettype_a11y')}
                                >
                                    <i className="fa fa-external-link" />
                                </Link>
                            )}
                        </span>
                    ))}
                    {restCount > 0 && (
                        <span className="small text-muted align-self-center">
                            {t('tickets.import.expected_id_list_rest', { count: restCount })}
                        </span>
                    )}
                </div>
            </InvalidKvRow>
            {bottomLinks}
        </div>
    );
};

const TicketsRawImporter = ({ visible }) => {
    const dispatch    = useDispatch();
    const { t, i18n } = useTranslation();

    const import_i18n_vars = React.useMemo(() => {
        const lng = i18n.language;
        const screening_label = t('common.screening_one');
        const screening = lng?.startsWith('de')
            ? screening_label
            : String(screening_label || '').toLocaleLowerCase(lng);
        return {
            screening,
            screening_label,
            mr: t('common.title.mr'),
            miss: t('common.title.miss'),
            mrs: t('common.title.mrs'),
        };
    }, [t, i18n.language]);

    const import_column_label = React.useCallback(
        (key) => t(`tickets.import.columns.${key}`, import_i18n_vars),
        [t, import_i18n_vars],
    );

    const import_column_help = React.useCallback(
        (key) => t(`tickets.import.column_help.${key}`, import_i18n_vars),
        [t, import_i18n_vars],
    );

    const sample_header_labels = React.useMemo(() => {
        const labels = {};
        CELLS.forEach((key) => {
            labels[key] = import_column_label(key);
        });
        return labels;
    }, [import_column_label]);

    const header_aliases = React.useMemo(() => {
        const aliases = {};
        const add = (label, key) => {
            const normalized = normalizeHeaderLabel(label);
            if (normalized)
                aliases[normalized] = key;
        };

        CELLS.forEach((key) => {
            add(key, key);
            add(LEGACY_HEADER_LABELS[key], key);
        });

        // Gender / Genre headers map to contact.sex (Sex kept for BC).
        ['sex', 'gender', 'genre', 'geschlecht', 'genere', LEGACY_HEADER_LABELS.sex]
            .forEach((label) => add(label, 'sex'));
        // Older EN sample wording before "booking notifications".
        add('Disable booking notifications', 'disable_email_notifications');
        add(LEGACY_HEADER_LABELS.disable_email_notifications, 'disable_email_notifications');

        IMPORT_HEADER_LANGS.forEach((lng) => {
            const screening_label = i18n.t('common.screening_one', { lng });
            const screening = String(lng || '').startsWith('de')
                ? screening_label
                : String(screening_label || '').toLocaleLowerCase(lng);
            const vars = {
                screening,
                screening_label,
                mr: i18n.t('common.title.mr', { lng }),
                miss: i18n.t('common.title.miss', { lng }),
                mrs: i18n.t('common.title.mrs', { lng }),
            };
            CELLS.forEach((key) => {
                add(i18n.t(`tickets.import.columns.${key}`, { lng, ...vars }), key);
            });
        });

        return aliases;
    }, [i18n]);

    const [rows, setRows]           = useState([]);
    const [headerRow, setHeaderRow] = useState([]);
    const [tickets, setTickets]     = useState([]);
    const [fileName, setFileName] = useState('');
    const [uploadError, setUploadError] = useState(null);
    const [omitEvents, setOmitEvents] = useState(false);
    const validationRunRef = useRef(0);

    const [detailsIndex, setDetailsIndex]  = useState(0);

    const {
        isImporting,
        hasImportingError,
        importingErrorMessage
    } = useSelector(state => state.tickets);

    const emails                   = useSelector(state => state.emailLayouts.layouts)
    const { user }                 = useSelector(state => state.auth);
    const { setting }              = useSelector(state => state.settings);
    const eshop_uri                = setting?.eshop?.uri;
    const { lastTicketImportLogs } = useSelector(state => state.logs);
    const { tickettypes }          = useSelector(state => state.tickettypes)
    const [import_screenings, set_import_screenings] = useState([]);
    const import_screenings_promise_ref = React.useRef(null);

    const ticketsImportLogs = lastTicketImportLogs?.filter(log => log.type === Log.TICKETS_IMPORT);

    useEffect(() => {
        if (!visible) {
            import_screenings_promise_ref.current = null;
            set_import_screenings([]);
            return undefined;
        }
        dispatch(loadTickettypes());
        dispatch(loadEmailLayouts());
        return undefined;
    }, [dispatch, visible]);

    // Future screenings are only needed to validate CSV rows (pricing / events).
    // Do not fetch while the sync panel is merely mounted/open.
    const load_import_screenings = React.useCallback(() => {
        if (import_screenings_promise_ref.current)
            return import_screenings_promise_ref.current;

        import_screenings_promise_ref.current = Screenings.list({
            start_at_gte: moment().toISOString(),
            fields: ['_id', 'refs', 'pricings', 'title', 'start_at', 'stop_at'],
        }).then((data) => {
            const list = data.screenings || [];
            set_import_screenings(list);
            return list;
        }).catch(() => {
            set_import_screenings([]);
            return [];
        });

        return import_screenings_promise_ref.current;
    }, []);

    const parseSpreadsheetFile = React.useCallback(async file => {
        const extension = file?.name?.split('.').pop()?.toLowerCase();
        if (!FILE_EXTENSIONS.includes(extension))
            throw new Error('tickets.import.unsupported_file_type');

        const fileBuffer = await file.arrayBuffer();
        let workbook = null;
        try {
            workbook = XLSX.read(fileBuffer, { type: 'array' });
        } catch (error) {
            throw new Error('tickets.import.invalid_file_content');
        }

        const firstSheetName = workbook.SheetNames?.[0];
        if (!firstSheetName)
            throw new Error('tickets.import.invalid_file_content');

        const firstSheet = workbook.Sheets[firstSheetName];
        const parsedRows = XLSX.utils.sheet_to_json(firstSheet, {
            header: 1,
            raw: false,
            defval: '',
            blankrows: false
        });

        return parsedRows;
    }, []);

    const onLoad = (rows, loadedFileName = '') => {
        if (!rows?.length)
            return;

        // remove last empty line if any
        if (rows[rows.length - 1].length === 1 && rows[rows.length - 1][0] === '')
            rows = rows.slice(0, -1);

        const normalizedRows = rows.map(row => row.map(sanitizeCell));
        setHeaderRow(normalizedRows[0] || []);
        setRows(normalizedRows.slice(1));
        setFileName(loadedFileName);
        setDetailsIndex(0);
        setUploadError(null);
        setOmitEvents(false);
    };

    const onFileSelected = async file => {
        if (!file)
            return;

        try {
            await load_import_screenings();
            const parsedRows = await parseSpreadsheetFile(file);
            onLoad(parsedRows, file.name || '');
        } catch (error) {
            setRows([]);
            setTickets([]);
            setDetailsIndex(0);
            setFileName('');
            setUploadError(error.message || 'tickets.import.invalid_file_content');
        }
    };

    const build_sample_import_rows = React.useCallback((screenings_list) => {
        let types = (tickettypes || [])
            .filter(type => type._id !== Tickettype.ONE_TIME_PASS)
            .map(type => type);

        const screening = (screenings_list || []).find(s => {
            return !!(Object.keys(s.pricings || {}).find(key => {
                const price = s.pricings[key]?.price;
                if (!price || typeof price !== 'object')
                    return false;
                return price[Object.keys(price)[0]] === 0;
            }));
        });
        const one_time_pass = (tickettypes || []).find(type => type._id === Tickettype.ONE_TIME_PASS);
        if (screening && one_time_pass) {
            const free_pricings = {};
            Object.keys(screening.pricings || {}).forEach(key => {
                const p = screening.pricings[key]?.price;
                if (p && p[Object.keys(p)[0]] === 0)
                    free_pricings[key] = screening.pricings[key];
            });
            if (Object.keys(free_pricings).length) {
                types = types.concat([{
                    ...one_time_pass,
                    screening_id: screening._id,
                    pricings: free_pricings,
                }]);
            }
        }
        if (!types?.length) {
            return [
                build_sample_row({
                    nb: '1',
                    type: 'festival_pass',
                    pricing: 'invitation',
                    firstname: 'Jane',
                    lastname: 'Doe',
                    email: 'jane.doe@gmail.com',
                    disable_email_notifications: 'false',
                    street: 'Avenue d\'Aïre 56',
                    zip: '1203',
                    city: 'Genève',
                    country: 'Suisse',
                    image_url: 'https://fastly.picsum.photos/id/866/200/300.jpg?hmac=rcadCENKh4rD6MAp6V_ma-AyWv641M4iiOpe1RyFHeI',
                    cellphone: '022 364 8000',
                    age: '42',
                    language: 'fr',
                    company: 'Net Oxygen',
                }, sample_header_labels),
            ];
        }

        const rows = [];
        types.forEach(tickettype => {
            Object.keys(tickettype.pricings || {}).forEach(pricing => {
                const is_otp = tickettype._id === Tickettype.ONE_TIME_PASS;
                const overrides = {
                    nb: '1',
                    type: preferred_public_id(tickettype._id, tickettype.refs),
                    pricing: preferred_public_id(pricing, tickettype.pricings?.[pricing]?.refs),
                    // OTP: single screening booking — disable emails by default.
                    // Multi-pass: keep notifications on unless the sheet says otherwise.
                    disable_email_notifications: is_otp ? 'true' : 'false',
                };

                if (user) {
                    overrides.firstname = user.firstname;
                    overrides.lastname  = user.lastname;
                    overrides.email     = user.email;
                    overrides.street    = user.address || '';
                    overrides.zip       = user.zip || '';
                    overrides.city      = user.city || '';
                    overrides.country   = user.country || '';
                    overrides.image_url = user.avatar || '';
                    overrides.cellphone = user.mobile_phone || '';
                    overrides.language  = i18n.language;
                }

                if (tickettype.screening_id) {
                    const sample_screening = (screenings_list || []).find(s => s._id === tickettype.screening_id);
                    overrides.screening = preferred_public_id(
                        sample_screening?._id || tickettype.screening_id,
                        sample_screening?.refs
                    );
                }

                rows.push(build_sample_row(overrides, sample_header_labels));
            });
        });

        return rows;
    }, [tickettypes, user, i18n.language, sample_header_labels]);

    const annotate_sample_sheet = React.useCallback((ws) => {
        fitWorksheetColumns(ws);
        annotateHeaderComments(ws, CELLS, (key) => {
            const help_key = `tickets.import.column_help.${key}`;
            const comment = import_column_help(key);
            return (!comment || comment === help_key) ? '' : comment;
        });
    }, [import_column_help]);

    const generateAndDownloadXlsx = async () => {
        const fileType      = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
        const fileExtension = '.xlsx';

        // OTP sample rows need future screenings with a free pricing — load them
        // here (not only on file upload), otherwise the template never includes OTP.
        const screenings_list = await load_import_screenings();
        const rows = build_sample_import_rows(screenings_list);

        const ws = XLSX.utils.json_to_sheet(rows);
        annotate_sample_sheet(ws);
        const wb = { Sheets: { 'Tickets': ws }, SheetNames: ['Tickets'] };
        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const data = new Blob([excelBuffer], {type: fileType});
        FileSaver.saveAs(data, `tickets_import${fileExtension}`);
    };

    const convertToTicket = data => {
        const normalizedData = Object.keys(data || {}).reduce((acc, key) => {
            acc[key] = sanitizeCell(data[key]);
            return acc;
        }, {});

        const ticket = {
            type: { _id: sanitizeIdentifier(normalizedData.type) },
            contact: {},
            opaque: {
                line: normalizedData.line
            }
        };

        if (sanitizeIdentifier(normalizedData.pricing).length)
            ticket.activable_pricings = [sanitizeIdentifier(normalizedData.pricing)];

        if (normalizedData.image_url?.length)
            ticket.opaque.image_url = normalizedData.image_url;

        if (sanitizeIdentifier(normalizedData.screening).length) {
            ticket.opaque.screening_id = sanitizeIdentifier(normalizedData.screening);
            if (ticket.type._id === Tickettype.ONE_TIME_PASS)
                ticket.type.screening_id = sanitizeIdentifier(normalizedData.screening);
        }

        [ 'email', 'firstname', 'lastname', 'language', 'sex', 'title', 'age', 'cellphone', 'company'].forEach(key => {
            if (normalizedData[key]?.length > 0)
                ticket.contact[key] = normalizedData[key];
        });

        ['zip', 'city', 'country'].forEach(key => {
            if (normalizedData[key]?.length > 0) {
                ticket.contact.address = ticket.contact.address || {};
                ticket.contact.address[key] = key === 'country'
                    ? (countryIsoFromValue(normalizedData[key]) || normalizedData[key])
                    : normalizedData[key];
            }
        });

        if (normalizedData.row?.length && normalizedData.seat?.length) {
            ticket.opaque.row_number = normalizedData.row;
            ticket.opaque.seat_number = normalizedData.seat;
        }

        if (normalizedData.disable_email_notifications?.length
            || normalizedData.disable_email_notifications === '0'
            || normalizedData.disable_email_notifications === 'false') {
            ticket.disable_email_notifications = normalizedData.disable_email_notifications;
        }

        ['window_nbookings', 'window_stop_at', 'wallet_amount'].forEach(key => {
            if (normalizedData[key]?.length > 0)
                ticket[key] = normalizedData[key];
        });

        const parsedTicketId = parseTicketId(normalizedData.ticket_id);
        if (parsedTicketId) {
            ticket.ticket_id         = normalizedData.ticket_id;
            ticket.vdr_auth_serial = parsedTicketId.vdr_auth_serial;
            ticket.vdr_auth_key    = parsedTicketId.vdr_auth_key;
        }

        return ticket;
    }

    const resetImportState = useCallback(() => {
        setRows([]);
        setHeaderRow([]);
        setTickets([]);
        setFileName('');
        setUploadError(null);
        setOmitEvents(false);
        setDetailsIndex(0);
    }, []);

    const cancel = useCallback(() => {
        resetImportState();
        dispatch(clearLastTicketImportLogs());
    }, [dispatch, resetImportState]);

    useEffect(() => {
        if (!visible)
            resetImportState();
    }, [visible, resetImportState]);

    const doImport = () => {
        const payload = tickets?.map(convertToTicket);
        dispatch(clearLastTicketImportLogs());
        dispatch(importTickets(payload, omitEvents ? { omit_events: true } : null));
    };

    useEffect(() => {
        const runId = ++validationRunRef.current;
        const check = async() => {
            const rememberScreening = (requestedId, screening) => {
                if (!screening?._id)
                    return null;
                screeningsCache[screening._id] = screening;
                if (requestedId)
                    screeningsCache[requestedId] = screening;
                (screening.refs || []).forEach(ref => {
                    const id = ref?.id != null ? String(ref.id).trim() : '';
                    if (id)
                        screeningsCache[id] = screening;
                });
                return screening;
            };

            if (import_screenings?.length) {
                import_screenings.forEach(screening => {
                    rememberScreening(screening?._id, screening);
                });
            }

            const ensureScreeningLoaded = async screeningId => {
                if (!screeningId?.length)
                    return null;

                const cached = screeningsCache[screeningId];
                if (cached?._ticketImportScreeningFetched)
                    return cached;

                const has_buckets = Array.isArray(cached?.buckets) && cached.buckets.length > 0;
                const has_pricings = Object.keys(cached?.pricings || {}).length > 0;
                if (cached && has_pricings && has_buckets)
                    return rememberScreening(screeningId, cached);

                try {
                    const result = await Screenings.get(screeningId, ['_id', 'refs', 'title', 'start_at', 'stop_at', 'pricings', 'buckets']);
                    const scr = result.screening || {};
                    scr._ticketImportScreeningFetched = true;
                    return rememberScreening(screeningId, scr);
                } catch (err) {
                    return null;
                }
            };

            const pushInvalid = (ticket, field, found, expected, extras = {}) => {
                ticket.invalidProperties.push({
                    field,
                    found: found ?? '',
                    expected: expected ?? '',
                    ...extras
                });
            };

            const knownScreeningIdsForExpected = () => {
                const ids = new Set();
                const seen = new Set();
                Object.values(screeningsCache).forEach(screening => {
                    if (!screening?._id || seen.has(screening._id))
                        return;
                    seen.add(screening._id);
                    ids.add(preferred_public_id(screening._id, screening.refs));
                });
                return [...ids];
            };

            const formatRestCount = rest =>
                t('tickets.import.expected_id_list_rest', { count: rest });

            let index = 0;
            const tickets = [];
            const screeningDemand = {};
            for (const row of rows) {
                if (runId !== validationRunRef.current)
                    return;
                const ticket = {
                    line: index + 2,
                    ...mapRowToCells(row, headerRow, header_aliases),
                };
                ticket.type = sanitizeIdentifier(ticket.type);
                ticket.pricing = sanitizeIdentifier(ticket.pricing);
                ticket.screening = sanitizeIdentifier(ticket.screening);
                ticket.nb = parseInt(ticket.nb, 10);
                if (!Number.isFinite(ticket.nb) || ticket.nb <= 0)
                    ticket.nb = 1;
                ticket.invalidProperties = [];

                if (ticket.email?.length > 0 && !Ticket.EMAIL_REGEX.test(ticket.email)) {
                    pushInvalid(
                        ticket,
                        'email',
                        ticket.email,
                        t('tickets.import.expected_valid_email')
                    );
                }

                if (ticket.disable_email_notifications?.length) {
                    if (parseBooleanCell(ticket.disable_email_notifications) === null) {
                        pushInvalid(
                            ticket,
                            'disable_email_notifications',
                            ticket.disable_email_notifications,
                            t('tickets.import.expected_boolean')
                        );
                    }
                }

                if (ticket.ticket_id?.length) {
                    const parsed = parseTicketId(ticket.ticket_id);
                    if (!parsed) {
                        pushInvalid(
                            ticket,
                            'ticket_id',
                            ticket.ticket_id,
                            t('tickets.import.expected_ticket_id')
                        );
                    } else {
                        ticket.vdr_auth_serial = parsed.vdr_auth_serial;
                        ticket.vdr_auth_key    = parsed.vdr_auth_key;
                    }
                    if (ticket.nb !== 1) {
                        pushInvalid(
                            ticket,
                            'nb',
                            String(ticket.nb),
                            t('tickets.import.expected_nb_one_with_ticket_id')
                        );
                    }
                }

                if (ticket.window_nbookings?.length) {
                    const parsed = Number(ticket.window_nbookings);
                    if (!Number.isFinite(parsed) || parsed < 0 || !Number.isInteger(parsed)) {
                        pushInvalid(
                            ticket,
                            'window_nbookings',
                            ticket.window_nbookings,
                            t('tickets.import.expected_non_negative_integer')
                        );
                    }
                }

                if (ticket.wallet_amount?.length) {
                    const parsed = Number(String(ticket.wallet_amount).replace(',', '.'));
                    if (!Number.isFinite(parsed) || parsed < 0) {
                        pushInvalid(
                            ticket,
                            'wallet_amount',
                            ticket.wallet_amount,
                            t('tickets.import.expected_non_negative_number')
                        );
                    }
                }

                if (ticket.window_stop_at?.length && !moment(ticket.window_stop_at).isValid()) {
                    pushInvalid(
                        ticket,
                        'window_stop_at',
                        ticket.window_stop_at,
                        t('tickets.import.expected_valid_date')
                    );
                }

                const rawType = ticket.type;
                const rawPricing = ticket.pricing;
                const rawScreening = ticket.screening;
                const tickettype = tickettypes?.find(tt => entity_matches_id_or_ref(tt, rawType));
                if (!tickettype) {
                    const typeIds = (tickettypes || [])
                        .map(tt => preferred_public_id(tt._id, tt.refs))
                        .filter(Boolean);
                    pushInvalid(
                        ticket,
                        'type',
                        rawType,
                        truncateSortedIds(typeIds, formatRestCount) || t('tickets.import.expected_tickettypes_fallback'),
                        {
                            idBadgeFound: rawType || null,
                            idBadgesExpected: [...typeIds].sort()
                        }
                    );
                } else {
                    ticket.type = tickettype._id;
                    if (tickettype._id === Tickettype.ONE_TIME_PASS) {
                        if (!rawScreening) {
                            pushInvalid(
                                ticket,
                                'screening',
                                rawScreening,
                                truncateSortedIds(knownScreeningIdsForExpected(), formatRestCount) ||
                                    t('tickets.import.expected_screening_id')
                            );
                        } else {
                            await ensureScreeningLoaded(rawScreening);
                            if (runId !== validationRunRef.current)
                                return;
                            const screening = screeningsCache[rawScreening];
                            if (screening?._id)
                                ticket.screening = screening._id;
                            if (screening) {
                                const pricingKey = find_pricing_key(screening.pricings, rawPricing);
                                if (!pricingKey) {
                                    pushInvalid(
                                        ticket,
                                        'pricing',
                                        rawPricing,
                                        joinPreferredPricingIds(screening.pricings) || t('tickets.import.expected_pricing_fallback'),
                                        {
                                            idBadgeFound: rawPricing || null,
                                            idBadgesExpected: preferredPricingIds(screening.pricings),
                                            screeningLink: { id: screening._id, tab: 'pricings' }
                                        }
                                    );
                                } else {
                                    ticket.pricing = pricingKey;
                                    const pricing = screening.pricings[pricingKey];
                                    const price = pricing.price[Object.keys(pricing.price)[0]];
                                    if (price !== 0) {
                                        const freeKeys = listPricingKeysWithZeroPrice(screening.pricings);
                                        const freeList = freeKeys.map(key => preferred_public_id(key, screening.pricings[key]?.refs));
                                        pushInvalid(
                                            ticket,
                                            'pricing',
                                            rawPricing,
                                            freeList.join(', ') || t('tickets.import.expected_free_pricing_otp'),
                                            {
                                                idBadgeFound: rawPricing || null,
                                                idBadgesExpected: freeList,
                                                screeningLink: { id: screening._id, tab: 'pricings' }
                                            }
                                        );
                                    }
                                }
                            }
                        }
                    } else {
                        const pricingKey = find_pricing_key(tickettype.pricings, rawPricing);
                        if (!pricingKey) {
                            pushInvalid(
                                ticket,
                                'pricing',
                                rawPricing,
                                joinPreferredPricingIds(tickettype.pricings) || t('tickets.import.expected_pricing_fallback'),
                                {
                                    idBadgeFound: rawPricing || null,
                                    idBadgesExpected: preferredPricingIds(tickettype.pricings),
                                    tickettypeLink: {
                                        id: tickettype._id,
                                        label: Utils.localized_or_fallback(tickettype.name, i18n.language) || rawType
                                    }
                                }
                            );
                        } else {
                            ticket.pricing = pricingKey;
                        }
                    }
                }

                if (rawScreening && ticket.screening === rawScreening && !hasInvalidField(ticket, 'screening')) {
                    await ensureScreeningLoaded(rawScreening);
                    if (runId !== validationRunRef.current)
                        return;
                    const loadedScreening = screeningsCache[rawScreening];
                    if (loadedScreening?._id)
                        ticket.screening = loadedScreening._id;
                }

                if (ticket.screening?.length && !hasInvalidField(ticket, 'screening')) {
                    if (!(ticket.screening in screeningDemand)) {
                        screeningDemand[ticket.screening] = {
                            count: 0,
                            tickettypes: new Set(),
                            lines: new Set()
                        };
                    }
                    screeningDemand[ticket.screening].count += ticket.nb;
                    screeningDemand[ticket.screening].lines.add(ticket.line);
                    screeningDemand[ticket.screening].lines.add(ticket.line);
                    if (ticket.type)
                        screeningDemand[ticket.screening].tickettypes.add(ticket.type);
                }

                for (let i = 0; i < ticket.nb; i++) {
                    tickets.push(ticket);
                }

                index++;
            }

            const screeningCapacityErrors = {};
            const screeningCapacityMeta = {};
            const screeningIds = Object.keys(screeningDemand);
            for (const screeningId of screeningIds) {
                const screening = await ensureScreeningLoaded(screeningId);
                if (runId !== validationRunRef.current)
                    return;
                if (!screening) {
                    screeningCapacityErrors[screeningId] = 'missing';
                    continue;
                }

                const remainingCapacity = getScreeningRemainingCapacity(
                    screening,
                    Array.from(screeningDemand[screeningId].tickettypes)
                );
                if (remainingCapacity === null)
                    continue;

                if (screeningDemand[screeningId].count > remainingCapacity) {
                    screeningCapacityErrors[screeningId] = 'capacity';
                    const linesSet = screeningDemand[screeningId].lines;
                    screeningCapacityMeta[screeningId] = {
                        demanded: screeningDemand[screeningId].count,
                        remaining: remainingCapacity,
                        multiLine: (linesSet?.size ?? 0) > 1
                    };
                }
            }

            if (Object.keys(screeningCapacityErrors).length) {
                const reportedScreeningMissing = new Set();
                const reportedScreeningCapacity = new Set();
                tickets.forEach(ticket => {
                    if (!(ticket.screening in screeningCapacityErrors))
                        return;
                    if (screeningCapacityErrors[ticket.screening] === 'missing' && !hasInvalidField(ticket, 'screening')) {
                        if (reportedScreeningMissing.has(ticket.screening))
                            return;
                        reportedScreeningMissing.add(ticket.screening);
                        if (reportedScreeningMissing.has(ticket.screening))
                            return;
                        reportedScreeningMissing.add(ticket.screening);
                        pushInvalid(
                            ticket,
                            'screening',
                            ticket.screening,
                            truncateSortedIds(knownScreeningIdsForExpected(), formatRestCount) ||
                                t('tickets.import.expected_screening_id'),
                            ticket.screening
                                ? { screeningLink: { id: ticket.screening, tab: 'general' } }
                                : {}
                        );
                    }
                    if (screeningCapacityErrors[ticket.screening] === 'capacity' && !hasInvalidField(ticket, 'screening_capacity')) {
                        if (reportedScreeningCapacity.has(ticket.screening))
                            return;
                        reportedScreeningCapacity.add(ticket.screening);
                        if (reportedScreeningCapacity.has(ticket.screening))
                            return;
                        reportedScreeningCapacity.add(ticket.screening);
                        const meta = screeningCapacityMeta[ticket.screening];
                        const scr = screeningsCache[ticket.screening];
                        const screeningName = Utils.localized_or_fallback(scr?.title, i18n.language) || ticket.screening;
                        pushInvalid(
                            ticket,
                            'screening_capacity',
                            '',
                            '',
                            {
                                screeningLink: { id: ticket.screening, tab: 'buckets' },
                                capacityPresentation: meta
                                    ? {
                                        demanded: meta.demanded,
                                        remaining: meta.remaining,
                                        multiLine: meta.multiLine,
                                        screeningName
                                    }
                                    : {
                                        demanded: 0,
                                        remaining: 0,
                                        multiLine: false,
                                        screeningName
                                    }
                            }
                        );
                    }
                });
            }

            if (runId !== validationRunRef.current)
                return;
            setTickets(tickets);
        };
        check();
    }, [rows, headerRow, header_aliases, tickettypes, import_screenings, t, i18n.language]);

    const emailsToSend = React.useCallback((tickettype, pricingKey) => {
        const toSend = [];

        if (!tickettype?.opaque?.emails || !tickettype.pricings[pricingKey])
            return toSend;

        const pricing = tickettype.pricings[pricingKey];
        const isFree = pricing.price[Object.keys(pricing.price)[0]] === 0;

        const base = (pricingKey in tickettype.opaque.emails) ?
            tickettype.opaque.emails[pricingKey] :
            tickettype.opaque.emails.default;

        if (base?.creation?.length) {
            const email = emails?.find(email => email._id === base.creation);
            if (email)
                toSend.push(email);
        }

        if (isFree && base?.activation?.length) {
            const email = emails?.find(email => email._id === base.activation);
            if (email)
                toSend.push(email);
        }

        return toSend;
    }, [emails]);

    const withError = useMemo(() => {
        if (!tickets?.length)
            return [];
        const seen = new Set();
        const out = [];
        for (const ticket of tickets) {
            if (!ticket.invalidProperties?.length)
                continue;
            if (seen.has(ticket))
                continue;
            seen.add(ticket);
            out.push(ticket);
        }
        return out;
    }, [tickets]);
    const hasEmailsToSend = React.useMemo(() => {
        if (!tickets?.length)
            return false;

        return tickets.some(ticket => {
            if (!ticket.email?.length)
                return false;
            const tickettype = tickettypes?.find(t => t._id === ticket.type);
            if (!tickettype)
                return false;

            return emailsToSend(tickettype, ticket.pricing).length > 0;
        });
    }, [tickets, tickettypes, emailsToSend]);

    const getImportErrorMessage = React.useCallback(() => {
        if (!importingErrorMessage)
            return t('tickets.import.an_error_occured');

        const translatedError = t(importingErrorMessage);
        if (translatedError !== importingErrorMessage)
            return translatedError;

        if (/^[a-z0-9_.-]+$/i.test(importingErrorMessage))
            return t('tickets.import.an_error_occured');

        return importingErrorMessage;
    }, [importingErrorMessage, t]);

    const renderErrors = React.useMemo(() => {
        const getInvalidFieldLabel = field => {
            const validationLabel = t(`tickets.import.validation.${field}`);
            if (validationLabel !== `tickets.import.validation.${field}`)
                return validationLabel;

            const columnLabel = import_column_label(field);
            if (columnLabel !== `tickets.import.columns.${field}`)
                return columnLabel;

            return field;
        };

        const formatFound = value =>
            (value !== null && value !== undefined && String(value).length > 0)
                ? String(value)
                : t('tickets.import.empty_cell');

        return (
            <Card className="mt-2 w-100 TicketsRawImporter-errorsCard">
                <CardBody>
                    {withError.map(ticket => (
                    <div key={`import-err-line-${ticket.line}`} className="TicketsRawImporter-errorLine">
                        <div className="TicketsRawImporter-errorLineHead">
                            <i className="fa fa-warning text-warning" aria-hidden />
                            <b>{ t('tickets.import.line_x', {line: ticket.line}) }</b>
                        </div>
                        { ticket.invalidProperties.map((entry, errIndex) => {
                            const field = entry.field;
                            const label = getInvalidFieldLabel(field);
                            return (
                        <div className="d-flex align-items-start TicketsRawImporter-importErrorRow" key={ `${ticket.line}-${field}-${errIndex}` }>
                            <i className="fa fa-arrow-right mr-2 mt-1 text-muted flex-shrink-0" aria-hidden />
                            <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                <TicketImportInvalidDetail
                                    entry={entry}
                                    label={label}
                                    formatFound={formatFound}
                                    t={t}
                                />
                            </div>
                        </div>
                            );
                        })}
                    </div>
                    ))}
                </CardBody>
            </Card>
        );
    }, [t, withError, import_column_label]);

    const renderResume = React.useMemo(() => {
        const getScreeningLabel = screeningId => {
            const screening = screeningsCache[screeningId];
            if (!screening)
                return screeningId;

            const title = Utils.localized_or_fallback(screening.title, i18n.language) || screeningId;
            const startAt = screening.start_at ? moment(screening.start_at) : null;
            if (startAt?.isValid())
                return `${title} (${startAt.format('lll')})`;
            return title;
        };

        const byTickettype = {};

        if (!tickets?.length)
            return null;

        tickets.forEach(ticket => {
            const tickettype = tickettypes?.find(t => t._id === ticket.type);
            if (!tickettype)
                return;

            let pricing = tickettype.pricings[ticket.pricing];
            if (tickettype._id === Tickettype.ONE_TIME_PASS) {
                const screening = screeningsCache[ticket.screening];
                if (!screening)
                    return;
                pricing = screening.pricings[ticket.pricing];
            }

            if (!pricing)
                return;

            if (!(tickettype._id in byTickettype))
                byTickettype[tickettype._id] = {
                    name: tickettype.name[i18n.language],
                    total: 0,
                    pricings: {}
                };

            if (!(ticket.pricing in byTickettype[tickettype._id].pricings))
                byTickettype[tickettype._id].pricings[ticket.pricing] = {
                    name: pricing.name[i18n.language],
                    total: 0,
                    emails: {},
                    screenings: {}
                };

            byTickettype[tickettype._id].total += 1;
            byTickettype[tickettype._id].pricings[ticket.pricing].total += 1;
            if (ticket.screening?.length) {
                if (!(ticket.screening in byTickettype[tickettype._id].pricings[ticket.pricing].screenings)) {
                    byTickettype[tickettype._id].pricings[ticket.pricing].screenings[ticket.screening] = {
                        total: 0,
                        label: getScreeningLabel(ticket.screening)
                    };
                }
                byTickettype[tickettype._id].pricings[ticket.pricing].screenings[ticket.screening].total += 1;
            }

            if (!ticket.email?.length)
                return;

            const emails = emailsToSend(tickettype, ticket.pricing);
            if (emails?.length) {
                emails.forEach(email => {
                    if (!(email._id in byTickettype[tickettype._id].pricings[ticket.pricing].emails))
                        byTickettype[tickettype._id].pricings[ticket.pricing].emails[email._id] = {
                            _id: email._id,
                            name: email.name[i18n.language],
                            total: 0
                        };

                    byTickettype[tickettype._id].pricings[ticket.pricing].emails[email._id].total += 1;
                });
            }
        });

        return (
            <Card className="mt-2 w-100">
                <CardBody>
                    <CardTitle>{t('tickets.import.resume_view')}</CardTitle>
                    {Object.values(byTickettype).map((type, index) => (
                        <div key={ index } className="mt-2">
                            <b>
                                <i className="fa fa-tags" />&nbsp;
                                { t('tickets.import.x_tickets_of_type', { count: type.total, name: type.name }) }
                            </b>
                            <div>
                                { Object.values(type.pricings).map((pricing, index) => (
                                <div className="ml-5" key={ index }>
                                    <i className="fa fa-dollar-sign" />&nbsp;
                                    { t('tickets.import.x_tickets_of_price', { count: pricing.total, name: pricing.name }) }
                                    <div>
                                        { Object.values(pricing.screenings || {}).map((screening, index) => (
                                        <div key={ `screening-${index}` } className="ml-5">
                                            <i className="fa fa-calendar" />&nbsp;
                                            { t('tickets.import.x_tickets_for_screening', {
                                                count: screening.total,
                                                screening: screening.label
                                            }) }
                                        </div>
                                        ))}
                                        { Object.values(pricing.emails).map((email, index) => (
                                        <div key={ index } className="ml-5">
                                            <i className="fa fa-envelope" />&nbsp;
                                            { t('tickets.import.send_x_emails', { count: email.total }) }&nbsp;
                                            <Button tag={Link} color="dark" size="xs" to={`/email_layouts/edit/${email._id}`}>
                                                <i className="fa fa-link" />&nbsp;
                                                { email.name }
                                            </Button>
                                        </div>
                                        ))}
                                    </div>
                                </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </CardBody>
            </Card>
        );
    }, [emailsToSend, i18n.language, t, tickets, tickettypes]);

    const previewData = React.useMemo(() => {
        const getScreeningLabel = screeningId => {
            if (!screeningId?.length)
                return '';

            const screening = screeningsCache[screeningId];
            if (!screening)
                return screeningId;

            const title = Utils.localized_or_fallback(screening.title, i18n.language) || screeningId;
            const startAt = screening.start_at ? moment(screening.start_at) : null;
            if (startAt?.isValid())
                return `${title} (${startAt.format('lll')})`;
            return title;
        };

        const getPricingLabel = ticket => {
            const tickettype = tickettypes?.find(type => type._id === ticket.type._id);
            if (!tickettype || !ticket.activable_pricings?.length)
                return null;

            const pricingKey = ticket.activable_pricings[0];
            if (ticket.type._id === Tickettype.ONE_TIME_PASS && ticket.opaque?.screening_id) {
                const screening = screeningsCache[ticket.opaque.screening_id];
                const screeningPricing = screening?.pricings?.[pricingKey];
                if (screeningPricing)
                    return Utils.localized_or_fallback(screeningPricing.name, i18n.language) || pricingKey;
            }

            const pricing = tickettype.pricings?.[pricingKey];
            return Utils.localized_or_fallback(pricing?.name, i18n.language) || pricingKey;
        };

        if (!tickets?.length)
            return null;

        if (detailsIndex > tickets?.length - 1) {
            setDetailsIndex(0);
            return null;
        }

        const ticket = convertToTicket(tickets[detailsIndex]);
        return (
            <Card className="mt-2 w-100">
                <CardBody>
                    <div className="d-flex gap-2 mb-2 justify-end">
                        <Button color="info" size="sm" disabled={detailsIndex === 0} onClick={() => setDetailsIndex(detailsIndex - 1)}>
                            <i className="fa fa-arrow-left" />
                        </Button>
                        <Button
                            color="info"
                            size="sm"
                            disabled={detailsIndex >= (tickets?.length ?? 0) - 1}
                            onClick={() => setDetailsIndex(detailsIndex + 1)}
                        >
                            <i className="fa fa-arrow-right" />
                        </Button>
                    </div>
                    <table className="table w-100">
                        <tbody>
                            <tr>
                                <th className="text-right" width="150px">{import_column_label('number')}</th>
                                <td className="text-left">{ detailsIndex + 1 }</td>
                            </tr>
                            <tr>
                                <th className="text-right" width="150px">{import_column_label('line')}</th>
                                <td className="text-left">{ ticket.opaque.line }</td>
                            </tr>
                            <tr>
                                <th className="text-right" width="150px">{import_column_label('type')}</th>
                                <td className="text-left">{ tickettypes?.find(type => type._id === ticket.type._id)?.name[i18n.language] }</td>
                            </tr>

                            { ticket.activable_pricings?.length > 0 && (
                            <tr>
                                <th className="text-right" width="150px">{import_column_label('pricing')}</th>
                                <td className="text-left">{ getPricingLabel(ticket) }</td>
                            </tr>
                            )}

                            { ticket.opaque?.screening_id?.length > 0 && (
                            <tr>
                                <th className="text-right" width="150px">{import_column_label('screening')}</th>
                                <td className="text-left">
                                    {`${ticket.opaque.screening_id} - ${getScreeningLabel(ticket.opaque.screening_id)}`}
                                </td>
                            </tr>
                            )}

                            {[ 'email', 'firstname', 'lastname', 'language', 'sex', 'title', 'age', 'cellphone', 'company'].map(key => (
                                <tr key={ key }>
                                    <th className="text-right" width="150px">{import_column_label(key)}</th>
                                    <td className="text-left">{ ticket.contact[key] }</td>
                                </tr>
                            ))}
                            {['zip', 'city', 'country'].map(key => (
                                <tr key={ key }>
                                    <th className="text-right" width="150px">{import_column_label(key)}</th>
                                    <td className="text-left">{
                                        key === 'country'
                                            ? countryDisplayName((ticket.contact?.address ?? {})[key], i18n.language)
                                            : (ticket.contact?.address ?? {})[key]
                                    }</td>
                                </tr>
                            ))}

                            { ticket.ticket_id?.length > 0 && (
                            <tr>
                                <th className="text-right" width="150px">{import_column_label('ticket_id')}</th>
                                <td className="text-left">{ ticket.ticket_id }</td>
                            </tr>
                            )}

                            { ticket.wallet_amount?.length > 0 && (
                            <tr>
                                <th className="text-right" width="150px">{import_column_label('wallet_amount')}</th>
                                <td className="text-left">{ ticket.wallet_amount }</td>
                            </tr>
                            )}

                            { ticket.window_nbookings?.length > 0 && (
                            <tr>
                                <th className="text-right" width="150px">{import_column_label('window_nbookings')}</th>
                                <td className="text-left">{ ticket.window_nbookings }</td>
                            </tr>
                            )}

                            { ticket.window_stop_at?.length > 0 && (
                            <tr>
                                <th className="text-right" width="150px">{import_column_label('window_stop_at')}</th>
                                <td className="text-left">
                                    { moment(ticket.window_stop_at).isValid()
                                        ? moment(ticket.window_stop_at).format('lll')
                                        : ticket.window_stop_at }
                                </td>
                            </tr>
                            )}
                        </tbody>
                    </table>
                    <div className="d-flex gap-2 mt-2 justify-end">
                        <Button color="info" size="sm" disabled={detailsIndex === 0} onClick={() => setDetailsIndex(detailsIndex - 1)}>
                            <i className="fa fa-arrow-left" />
                        </Button>
                        <Button
                            color="info"
                            size="sm"
                            disabled={detailsIndex >= (tickets?.length ?? 0) - 1}
                            onClick={() => setDetailsIndex(detailsIndex + 1)}
                        >
                            <i className="fa fa-arrow-right" />
                        </Button>
                    </div>
                </CardBody>
            </Card>
        );
    }, [tickets, detailsIndex, tickettypes, i18n.language, import_column_label]);

    const ticketInfo = useCallback(ticket => {
        const fullname = [
            ticket.contact?.firstname,
            ticket.contact?.lastname
        ].filter(Boolean).join(' ').trim();
        return [
            (ticket.type?.name || {})[i18n.language],
            fullname.length > 0 ? fullname : ticket.contact?.email
        ].map(s => s?.trim()).filter(Boolean).join(' - ');
    }, [i18n.language]);

    const importResume = React.useMemo(() => log => (
        <div key={log._id}>
            <div className="bg-dark rounded w-100 p-2">
                <h5>
                    { t('tickets.import.resume') }
                </h5>
                <ul>
                    <li>
                        <b>{ t('tickets.import.nb_inserted', { count: log.inserted?.length }) }</b>
                    </li>
                    <li>
                        <b>{ t('tickets.import.nb_activated', { count: log.activated?.length }) }</b>
                    </li>
                    <li>
                        <b>{ t('tickets.import.nb_updated', { count: log.updated?.length }) }</b>
                    </li>
                    <li>
                        <b>{ t('tickets.import.nb_invalid', { count: log.invalid?.length }) }</b>
                    </li>
                </ul>
            </div>
            <Button onClick={ cancel } color="warning" title={t("tickets.import.do_another_import")} className="mt-2">
                <i className="fa fa-arrow-left"></i>&nbsp;{t('tickets.import.do_another_import')}
            </Button>
            {(log.activated?.length > 0) && (
                <div className="bg-info mt-2 p-2">
                    <h5>
                        { t('tickets.import.activated', { count: log.activated.length }) }
                    </h5>
                    <ul>
                    {log.activated.map(ticket => (
                        <li key={ticket._id} className="ticket">
                            <a target="_blank" rel="noopener noreferrer" href={`${eshop_uri}/ticket/view/?uuid=${ticket._id}`}>
                                <b>
                                    { ticketInfo(ticket) }
                                </b>
                            </a>
                        </li>
                    ))}
                    </ul>
                </div>
            )}
            {(log.inserted?.length > 0) && (
                <div className="bg-info mt-2 p-2">
                    <h5>
                        { t('tickets.import.inserted', { count: log.inserted.length }) }
                    </h5>
                    <ul>
                    {log.inserted.map(ticket => (
                        <li key={ticket._id} className="ticket">
                            <a target="_blank" rel="noopener noreferrer" href={`${eshop_uri}/ticket/view/?uuid=${ticket._id}`}>
                                <b>
                                    { ticketInfo(ticket) }
                                </b>
                            </a>
                        </li>
                    ))}
                    </ul>
                </div>
            )}
            {(log.updated?.length > 0) && (
                <div className="bg-info mt-2 p-2">
                    <h5>
                        { t('tickets.import.updated', { count: log.updated.length }) }
                    </h5>
                    <ul>
                    {log.updated.map(ticket => (
                        <li key={ticket._id} className="ticket">
                            <a target="_blank" rel="noopener noreferrer" href={`${eshop_uri}/ticket/view/?uuid=${ticket._id}`}>
                                <b>
                                    { ticketInfo(ticket) }
                                </b>
                            </a>
                        </li>
                    ))}
                    </ul>
                </div>
            )}
            {(log.invalid?.length > 0) && (
                <div className="bg-danger mt-2 p-2">
                    <h5>
                        { t('tickets.import.invalid', { count: log.invalid.length }) }
                    </h5>
                    {log.invalid.map((ticket, i) => (
                        <div key={i} className="ticket">
                            {ticket._id ? (
                                <a target="_blank" rel="noopener noreferrer" href={`${eshop_uri}/ticket/view/?uuid=${ticket._id}`}>
                                    <b>
                                        { ticketInfo(ticket) }
                                    </b>
                                </a>
                            ) : (
                                <b>
                                    { ticketInfo(ticket) }
                                </b>
                            )}
                            - { ticket._id }
                            <ul>
                                {Object.keys(ticket.invalid).map(key => (
                                    <ImportInvalidMessage
                                        key={key}
                                        resource="tickets"
                                        fieldKey={key}
                                        code={ticket.invalid[key]}
                                        importSource={log?.source || 'raw'}
                                        entry={ticket}
                                    />
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            )}
        </div>
    ), [cancel, eshop_uri, t, ticketInfo]);


    if (!visible)
        return null;

    return (
        <div className="TicketsImport">
            {(isImporting) ? (
                <div className="d-flex justify-center">
                    <i className="fa fa-spinner fa-spin fa-3x fa-fw"></i>
                </div>
            ) : (
                <div>
                    {hasImportingError && (
                        <div className="mb-2 bg-danger p-2 rounded">
                            { getImportErrorMessage() }
                        </div>
                    )}

                    {ticketsImportLogs?.length > 0 ? (
                        <div>
                            { ticketsImportLogs?.map(log => importResume(log)) }
                            <Button onClick={ cancel } color="warning" title={t("tickets.import.do_another_import")} className="mt-2">
                                <i className="fa fa-arrow-left"></i>&nbsp;{t('tickets.import.do_another_import')}
                            </Button>
                        </div>
                    ) : (
                        <div>
                            {(withError?.length > 0 || !tickets?.length) && (
                            <div className="d-flex column">
                                <SyncFileImportSection
                                    className="w-100"
                                    title={ t('tickets.import.title') }
                                    help={ t('tickets.import.desc') }
                                    formats={ t('common.supported_spreadsheet_formats') }
                                    accept=".csv,.xls,.xlsx,.ods,.numbers"
                                    fileName={ fileName }
                                    onFile={ onFileSelected }
                                    downloadSlot={
                                        <Button color="info" size="sm" onClick={ generateAndDownloadXlsx }>
                                            <i className="fa fa-download" aria-hidden="true" />
                                            &nbsp;{ t('tickets.import.download_xlsx_file') }
                                        </Button>
                                    }
                                >
                                    { uploadError && (
                                        <div className="mt-2 text-danger">
                                            { t(uploadError) !== uploadError ? t(uploadError) : t('tickets.import.invalid_file_content') }
                                        </div>
                                    )}
                                </SyncFileImportSection>
                            </div>
                            )}

                            {withError?.length > 0 && (
                                <div className="d-flex column">
                                    <h4 className="mt-4 mb-2">
                                        <i className="fa fa-warning" />&nbsp;
                                        {t('tickets.import.file_errors', {nb: withError.length})}
                                        <br />
                                        <small className="d-inline-block mt-1 font-weight-normal"><i>{t('tickets.import.file_errors_desc')}</i></small>
                                    </h4>
                                    { renderErrors }
                                </div>
                            )}

                            {(tickets?.length > 0) && (!withError?.length) && (
                                <div className="d-flex column">
                                    <h4>
                                        <i className="fa fa-check" />&nbsp;
                                        {t('tickets.import.file_goods', {nb: tickets.length})}<br />
                                    </h4>
                                    {hasEmailsToSend && (
                                        <div className="form-check mt-2">
                                            <input
                                                id="ticket-import-omit-events"
                                                className="form-check-input"
                                                type="checkbox"
                                                checked={omitEvents}
                                                onChange={event => setOmitEvents(event.target.checked)}
                                            />
                                            <label className="form-check-label" htmlFor="ticket-import-omit-events">
                                                {t('tickets.import.omit_events')}
                                            </label>
                                        </div>
                                    )}
                                    <ButtonGroup className="mt-3 mb-3">
                                        <Button onClick={ cancel } color="danger" title={t("common.cancel")}>
                                            <i className="fa fa-times"></i>&nbsp;{t('common.cancel')}
                                        </Button>
                                        <Button onClick={ doImport } color="success" title={t("tickets.import.do_import")}>
                                            <i className="fa fa-upload"></i>&nbsp;{t('tickets.import.do_import')}
                                        </Button>
                                    </ButtonGroup>

                                    { renderResume }
                                    { previewData }

                                    <ButtonGroup className="mt-3 mb-3">
                                        <Button onClick={ cancel } color="danger" title={t("common.cancel")}>
                                            <i className="fa fa-times"></i>&nbsp;{t('common.cancel')}
                                        </Button>
                                        <Button onClick={ doImport } color="success" title={t("tickets.import.do_import")}>
                                            <i className="fa fa-upload"></i>&nbsp;{t('tickets.import.do_import')}
                                        </Button>
                                    </ButtonGroup>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default TicketsRawImporter;
